import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useWeb5Context } from 'lib/Web5Context';

let initialState = {
    filters: {},
    operators: {},
    crossFilters: {},
    crossOperators: {},
    visibleFilters: [],
    openFilters: {},
    openQuickFilters: {},
    quickFilters: [],
};

const defaultfiltersControl = {
    filters: {},
    operators: {},
    crossFilters: {},
    crossOperators: {},
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT':
            return {
                ...state,
                filters: action.filters || initialState.filters,
                crossFilters: action.crossFilters || initialState.crossFilters,
                operators: action.operators || initialState.operators,
                crossOperators: action.crossOperators || initialState.crossOperators,
                visibleFilters: action.visibleFilters || initialState.visibleFilters,
                openFilters: action.openFilters || initialState.openFilters,
                openQuickFilters: action.openQuickFilters || initialState.openQuickFilters,
                quickFilters: action.quickFilters || initialState.quickFilters,
            };
        case 'CHANGE_FILTER':
            return {
                ...state,
                filters: action.filters,
            };
        case 'CHANGE_CROSS_FILTER':
            return {
                ...state,
                crossFilters: {
                    ...state.crossFilters,
                    [action.crossEntity]: action.filters,
                },
            };
        case 'CHANGE_OPERATORS':
            return {
                ...state,
                operators: action.operators,
            };
        case 'CHANGE_FILTER_AND_OPERATOR':
            return {
                ...state,
                filters: action.filters,
                operators: action.operators,
            };
        case 'CHANGE_CROSS_OPERATORS':
            return {
                ...state,
                crossOperators: { ...state.crossOperators, [action.crossEntity]: action.operators },
            };
        case 'CHANGE_CROSS_FILTER_AND_CROSS_OPERATOR':
            return {
                ...state,
                crossFilters: {
                    ...state.crossFilters,
                    [action.crossEntity]: action.filters,
                },
                crossOperators: { ...state.crossOperators, [action.crossEntity]: action.operators },
            };
        case 'CLEAR_ALL':
            return {
                ...state,
                filters: {},
                operators: {},
                crossFilters: {},
                crossOperators: {},
            };
        case 'HARD_RESET':
            return {
                ...initialState,
            };
        case 'UPDATE_CONFIG':
        default:
            return {
                ...state,
                visibleFilters: action.hasOwnProperty('visibleFilters')
                    ? action.visibleFilters
                    : state.visibleFilters,
                openFilters: action.hasOwnProperty('openFilters')
                    ? action.openFilters
                    : state.openFilters,
                openQuickFilters: action.hasOwnProperty('openQuickFilters')
                    ? action.openQuickFilters
                    : state.openQuickFilters,
                quickFilters: action.hasOwnProperty('quickFilters')
                    ? action.quickFilters
                    : state.quickFilters,
            };
    }
};

// This allows to handle filters being independent from the normal storage
const useForceBIFilters = ({
    defaultCache,
    afterOnChange,
    afterOnChangeCache,
    constants,
    isDashboard,
}) => {
    const { entities } = useWeb5Context();

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        ...(defaultCache || {}),
    });

    const hasBeenInitialized = useRef(false);
    const filtersControlRef = useRef({ ...defaultfiltersControl });

    const initFiltersRefs = useCallback(({ filters, operators, crossFilters, crossOperators }) => {
        if (filters) filtersControlRef.current.filters = filters;
        if (operators) filtersControlRef.current.operators = operators;
        if (crossFilters) filtersControlRef.current.crossFilters = crossFilters;
        if (crossOperators) filtersControlRef.current.crossOperators = crossOperators;
    }, []);

    const updateFiltersRefs = useCallback(({ filters, operators, crossEntity, isAPurge }) => {
        if (isAPurge) {
            filtersControlRef.current = { ...defaultfiltersControl };
        }
        if (!!crossEntity) {
            if (filters) {
                filtersControlRef.current = {
                    ...filtersControlRef.current,
                    crossFilters: {
                        ...filtersControlRef.current.crossFilters,
                        [crossEntity]: filters,
                    },
                };
            }
            if (operators) {
                filtersControlRef.current = {
                    ...filtersControlRef.current,
                    crossOperators: {
                        ...filtersControlRef.current.crossOperators,
                        [crossEntity]: operators,
                    },
                };
            }
        } else {
            filtersControlRef.current = {
                ...filtersControlRef.current,
                filters: filters ? filters : filtersControlRef.current.filters,
                operators: operators ? operators : filtersControlRef.current.operators,
            };
        }
    }, []);

    const dispatchFilters = useCallback(
        ({ type, filters, operators, crossEntity, isAPurge }) => {
            if (isAPurge) {
                updateFiltersRefs({ isAPurge });
                dispatch({ type });
                return;
            }
            updateFiltersRefs({ filters, operators, crossEntity });
            dispatch({ type, filters, operators, crossEntity });
        },
        [updateFiltersRefs],
    );

    const reset = useCallback(() => {
        dispatch({ type: 'HARD_RESET' });
        initFiltersRefs({ ...defaultfiltersControl });
        hasBeenInitialized.current = false;
    }, [initFiltersRefs]);

    const init = useCallback(
        ({
            filters,
            operators,
            crossFilters,
            crossOperators,
            visibleFilters,
            openFilters,
            openQuickFilters,
            quickFilters,
        }) => {
            if (hasBeenInitialized?.current) return;
            hasBeenInitialized.current = true;

            initFiltersRefs({ filters, operators, crossFilters, crossOperators });

            dispatch({
                type: 'INIT',
                filters,
                operators,
                crossFilters,
                crossOperators,
                visibleFilters,
                openFilters,
                openQuickFilters,
                quickFilters,
            });
        },
        [initFiltersRefs],
    );

    const handleAfterOnChange = useCallback(
        ({ newFilters, newOperators, crossEntity, isAPurge }) => {
            if (afterOnChange) {
                let uiFilters = {};
                if (isAPurge) {
                    uiFilters = {
                        filters: {},
                        crossFilters: {},
                        operators: {},
                        crossOperators: {},
                    };

                    afterOnChange(uiFilters);
                    return;
                }
                const filtersControl = filtersControlRef.current;

                if (newFilters || newOperators) {
                    if (!!crossEntity) {
                        if (newFilters && newOperators) {
                            uiFilters = {
                                filters: filtersControl.filters,
                                crossFilters: {
                                    ...filtersControl.crossFilters,
                                    [crossEntity]: newFilters,
                                },
                                operators: filtersControl.operators,
                                crossOperators: {
                                    ...filtersControl.crossOperators,
                                    [crossEntity]: newOperators,
                                },
                            };
                        } else if (newFilters && !newOperators) {
                            uiFilters = {
                                filters: filtersControl.filters,
                                crossFilters: {
                                    ...filtersControl.crossFilters,
                                    [crossEntity]: newFilters,
                                },
                                operators: filtersControl.operators,
                                crossOperators: filtersControl.crossOperators,
                            };
                        } else if (!newFilters && newOperators) {
                            uiFilters = {
                                filters: filtersControl.filters,
                                crossFilters: filtersControl.crossFilters,
                                operators: filtersControl.operators,
                                crossOperators: {
                                    ...filtersControl.crossOperators,
                                    [crossEntity]: newOperators,
                                },
                            };
                        }
                    } else {
                        if (newFilters && newOperators) {
                            uiFilters = {
                                filters: newFilters,
                                crossFilters: filtersControl.crossFilters,
                                operators: newOperators,
                                crossOperators: filtersControl.crossOperators,
                            };
                        } else if (newFilters && !newOperators) {
                            uiFilters = {
                                filters: newFilters,
                                crossFilters: filtersControl.crossFilters,
                                operators: filtersControl.operators,
                                crossOperators: filtersControl.crossOperators,
                            };
                        } else if (!newFilters && newOperators) {
                            uiFilters = {
                                filters: filtersControl.filters,
                                crossFilters: filtersControl.crossFilters,
                                operators: newOperators,
                                crossOperators: filtersControl.crossOperators,
                            };
                        }
                    }
                } else {
                    uiFilters = {
                        filters: {},
                        crossFilters: {},
                        operators: {},
                        crossOperators: {},
                    };
                }

                afterOnChange(uiFilters);
            }
        },
        [afterOnChange],
    );

    const handleWhileOnChange = useCallback(
        ({ filter, value, filters, crossEntity }) => {
            const { ACTIVITIES } = entities;
            let newFilters = { ...filters };

            if (crossEntity === ACTIVITIES.entity) {
                const activitiesConstants = constants[ACTIVITIES.entity];
                const { mappedActivityTypeIdsReverse } = activitiesConstants;

                const filterId = filter.id;

                const subFilters = [
                    'listCheckInType',
                    'fastCheckIn',
                    'opportunityCheckIn',
                    'callType',
                    'videoCallCheckInType',
                    'videoCallType',
                    'type',
                    'email',
                    'whatsappType',
                ];

                if (filterId !== 'activityType') return newFilters;

                // if (
                //     (newFilters?.isSuspiciousCheckIn && !newFilters?.activityType) ||
                //     (!newFilters.activityType?.value?.includes(
                //         activityTypes.ACTIVITY_TYPE_CHECKIN.toString(),
                //     ) &&
                //         !newFilters.activityType?.value?.includes(
                //             activityTypes.ACTIVITY_TYPE_OPPORTUNITY_CHECKIN.toString(),
                //         ))
                // ) {
                //     // When there is isSuspiciousCheckIn filter and there are no CheckIn and
                //     // OpportunityCheckIn filters, remove isSuspiciousCheckIn filter
                //     delete newFilters.isSuspiciousCheckIn;
                // }

                const activeSubFilters = Object.keys(newFilters).reduce((arr, key) => {
                    if (subFilters.includes(key)) arr.push(key);
                    return arr;
                }, []);

                if (activeSubFilters.length) {
                    if (!value || !value?.length) {
                        newFilters = Object.entries(newFilters).reduce((obj, [key, value]) => {
                            if (!subFilters.includes(key)) obj[key] = value;
                            return obj;
                        }, {});
                    } else {
                        const activityTypeValues = value || [];
                        const subFiltersToRemove = activeSubFilters.reduce((arr, current) => {
                            const filterId = mappedActivityTypeIdsReverse[current];
                            if (!activityTypeValues.includes(filterId.toString())) {
                                arr.push(current);
                            }
                            return arr;
                        }, []);

                        newFilters = Object.entries(newFilters).reduce((obj, [key, value]) => {
                            if (!subFiltersToRemove.includes(key)) obj[key] = value;
                            return obj;
                        }, {});
                    }
                }
            }

            return newFilters;
        },
        [entities, constants],
    );

    const customChangeFilter = useCallback(
        ({ filter, value, completeValues = null, crossEntity }) => {
            let newFilters;
            // use filtersControlRef because when triggering change filters sequentially the state has no time
            // to update and then we loose data. filtersControlRef is always updated
            let filters = filtersControlRef.current.filters;
            let crossFilters = filtersControlRef.current.crossFilters;

            filters = crossEntity ? (crossFilters?.[crossEntity] ?? {}) : (filters ?? {});

            if (
                !value ||
                value.length === 0 ||
                value === '' ||
                (value.hasOwnProperty('from') && !value.from && !value.to)
            ) {
                const { [filter.id]: value, ...newFiltersRest } = filters;
                newFilters = newFiltersRest;
                if (filter.hasOwnProperty('extraFilterProps')) {
                    const { [filter.extraFilterProps.id]: value, ...newFiltersWithoutExtra } =
                        newFilters;
                    newFilters = newFiltersWithoutExtra;
                }
            } else {
                if (filter?.isCustomRange) {
                    newFilters = {
                        ...filters,
                        [filter.id]: {
                            value,
                            label: completeValues.label,
                            completeValues,
                            ...filter,
                        },
                    };
                } else {
                    newFilters = {
                        ...filters,
                        [filter.id]: {
                            value,
                            completeValues,
                            ...filter,
                        },
                    };
                }
            }

            newFilters = handleWhileOnChange({ filter, value, filters: newFilters, crossEntity });

            if (!!crossEntity) {
                dispatchFilters({ type: 'CHANGE_CROSS_FILTER', filters: newFilters, crossEntity });
            } else {
                dispatchFilters({ type: 'CHANGE_FILTER', filters: newFilters });
            }

            handleAfterOnChange({ newFilters, crossEntity });
        },
        [handleAfterOnChange, handleWhileOnChange, dispatchFilters],
    );

    const customChangeOperator = useCallback(
        ({ operators, crossEntity }) => {
            if (!!crossEntity) {
                dispatchFilters({
                    type: 'CHANGE_CROSS_OPERATORS',
                    operators,
                    crossEntity: crossEntity.entity || crossEntity,
                });
            } else {
                dispatchFilters({ type: 'CHANGE_OPERATORS', operators });
            }

            handleAfterOnChange({ newOperators: operators, crossEntity });
        },
        [handleAfterOnChange, dispatchFilters],
    );

    const customDeleteMultipleFilters = useCallback(
        ({ entity, filters }) => {
            const name = entity.entity;
            const {
                filters: oldFilters = {},
                operators: oldOperators = {},
                crossFilters: oldCrossFilters = {},
                crossOperators: oldCrossOperators = {},
            } = state;
            const isCrossFilters = !Array.isArray(filters);

            const deleteFilters = (filtersArr, filters, operators) => {
                const newFilters = Object.keys(filters).reduce((obj, key) => {
                    if (!filtersArr.includes(key)) obj[key] = filters[key];
                    return obj;
                }, {});

                const newOperators = Object.keys(operators).reduce((obj, key) => {
                    if (!filtersArr.includes(key)) obj[key] = operators[key];
                    return obj;
                }, {});

                return { newFilters, newOperators };
            };

            const processFilters = (filtersArr, currentFilters, currentOperators, crossEntity) => {
                const { newFilters, newOperators } = deleteFilters(
                    filtersArr,
                    currentFilters,
                    currentOperators,
                );

                if (!!crossEntity) {
                    dispatchFilters({
                        type: 'CHANGE_CROSS_FILTER_AND_CROSS_OPERATOR',
                        filters: newFilters,
                        operators: newOperators,
                        crossEntity,
                    });
                } else {
                    dispatchFilters({
                        type: 'CHANGE_FILTER_AND_OPERATOR',
                        filters: newFilters,
                        operators: newOperators,
                    });
                }

                handleAfterOnChange({ newFilters, newOperators, crossEntity });
            };

            if (isCrossFilters) {
                for (const [crossEntity, crossEntityFilters] of Object.entries(filters)) {
                    const isCrossEntity = name !== crossEntity;
                    const currentFilters = isCrossEntity
                        ? oldCrossFilters[crossEntity] || {}
                        : oldFilters;
                    const currentOperators = isCrossEntity
                        ? oldCrossOperators[crossEntity] || {}
                        : oldOperators;
                    processFilters(
                        crossEntityFilters,
                        currentFilters,
                        currentOperators,
                        isCrossEntity ? crossEntity : null,
                    );
                }
            } else {
                if (filters.length === 0) return;
                processFilters(filters, oldFilters, oldOperators);
            }
        },
        [state, handleAfterOnChange, dispatchFilters],
    );

    const customClearFilters = useCallback(() => {
        dispatchFilters({ type: 'CLEAR_ALL', isAPurge: true });
        handleAfterOnChange({ isAPurge: true });
    }, [handleAfterOnChange, dispatchFilters]);

    const customSaveCache = useCallback(
        (newCache = {}) => {
            const compareFields = [
                'visibleFilters',
                'openFilters',
                'openQuickFilters',
                'quickFilters',
            ];
            const payload = compareFields.reduce((obj, field) => {
                obj[field] = newCache[field] || state[field];
                return obj;
            }, {});

            const oldVisibleFilters = [...state.visibleFilters];

            dispatch({ type: 'UPDATE_CONFIG', ...payload });

            afterOnChangeCache &&
                afterOnChangeCache({ visibleFilters: payload.visibleFilters, oldVisibleFilters });
        },
        [state, afterOnChangeCache],
    );

    const hasFilters = useMemo(() => {
        return Object.keys(state.filters)?.length > 0;
    }, [state.filters]);

    const hasOperators = useMemo(() => {
        return Object.keys(state.operators)?.length > 0;
    }, [state.operators]);

    const hasCrossFilters = useMemo(() => {
        return Object.entries(state.crossFilters).some(
            ([key, value]) => Object.keys(value).length > 0,
        );
    }, [state.crossFilters]);

    const hasCrossOperators = useMemo(() => {
        return Object.entries(state.crossOperators).some(
            ([key, value]) => Object.keys(value).length > 0,
        );
    }, [state.crossOperators]);

    const hasActiveFilters = useMemo(() => {
        return hasFilters || hasOperators || hasCrossFilters || hasCrossOperators;
    }, [hasFilters, hasOperators, hasCrossFilters, hasCrossOperators]);

    // WIP allow isSuspiciousCheckIn filter
    // const customIsSuspiciousCheckInOnClick = useCallback(() => {
    //     const { ACTIVITIES } = entities;
    //     const crossFilters = state.crossFilters;
    //     const entityCrossFilters = crossFilters?.[ACTIVITIES.entity] || {};
    //     const isSuspiciousCheckIn = entityCrossFilters?.isSuspiciousCheckIn || {};

    //     if (isSuspiciousCheckIn?.value) {
    //         customChangeFilter({
    //             filter: { id: 'isSuspiciousCheckIn' },
    //             value: 1,
    //             completeValues: 1,
    //             crossEntity: ACTIVITIES.entity,
    //         });
    //     } else {
    //         customChangeFilter({
    //             filter: { id: 'isSuspiciousCheckIn' },
    //             value: 0,
    //             completeValues: 0,
    //             crossEntity: ACTIVITIES.entity,
    //         });
    //     }
    // }, [state.crossFilters, customChangeFilter, entities]);

    const treatSchema = useCallback(
        ({ schema, entity }) => {
            // In ForceBI the principal filter is always ACCOUNTS the rest are cross filters
            // When is sent to backend, all filters are sent grouped by entity without the principal entity
            const filtersToIgnore = ['onlySelected', 'idCampaign'];
            const { ACTIVITIES } = entities;
            const crossFiltersToIgnore = {
                [ACTIVITIES.entity]: [
                    'customActivities',
                    'FollowingItem',
                    'pendingConversations',
                    'subject',
                ],
            };

            const finalSchema = schema.reduce((arr, current) => {
                if (filtersToIgnore.includes(current.id)) return arr;
                if (entity && crossFiltersToIgnore?.[entity]?.includes(current.id)) return arr;
                const filter = { ...current };
                // We don't need this isFakeCrossFilter prop. ForceBI Backend accepts those filters like they are, without transformations.
                if (filter.isFakeCrossFilter) filter.isFakeCrossFilter = false;
                filter.isRemovable = true;
                filter.isPinnable = false;

                if (filter?.inputAttrs?.actions) {
                    //Hide isSuspiciousCheckIn filter until its implemented
                    delete filter.inputAttrs.actions;
                    // WIP allow isSuspiciousCheckIn filter
                    // filter.inputAttrs?.actions.forEach((current) => {
                    //     if (current.id === 'isSuspiciousCheckIn') {
                    //         current.onClick = customIsSuspiciousCheckInOnClick;
                    //     }
                    // });
                }
                arr.push(filter);
                return arr;
            }, []);

            return finalSchema;
        },
        [entities],
    );

    const treatExtraSchema = useCallback(({ tabs, entity }) => {
        const finalTabs = tabs.map((tab) => {
            const newFields = tab.fields.reduce((arr, field) => {
                const newField = { ...field };
                newField.isRemovable = true;
                newField.isPinnable = false;
                arr.push(newField);
                return arr;
            }, []);

            return { ...tab, fields: newFields };
        });
        return finalTabs;
    }, []);

    const beforeSchema = useCallback(
        (schema) => {
            return treatSchema({ schema });
        },
        [treatSchema],
    );

    const beforeExtraSchema = useCallback(
        (tabs) => {
            return treatExtraSchema({ tabs });
        },
        [treatExtraSchema],
    );

    const beforeCrossSchema = useCallback(
        ({ schema, entity }) => {
            return treatSchema({ schema, entity });
        },
        [treatSchema],
    );

    const beforeCrossExtraSchema = useCallback(
        ({ tabs, entity }) => {
            return treatExtraSchema({ tabs, entity });
        },
        [treatExtraSchema],
    );

    const customGetSubFilterParentValue = useCallback((filter) => {
        if (filter?.completeValues?.length) {
            return filter?.completeValues?.map((completeValue) => completeValue.value);
        }
        return null;
    }, []);

    return {
        // This prop is key to make NewEntityFilters avoid normal storage (cache and store)
        // The following methods take control of the storage that will be handled from this hook
        avoidStorage: true,
        customChangeFilter,
        customDeleteMultipleFilters,
        customClearFilters,
        customChangeOperator,
        customSaveCache,
        customGetSubFilterParentValue,
        init,
        reset,
        beforeSchema,
        beforeExtraSchema,
        beforeCrossSchema,
        beforeCrossExtraSchema,
        hasActiveFilters,
        filters: state.filters,
        operators: state.operators,
        crossFilters: state.crossFilters,
        crossOperators: state.crossOperators,
        customCache: {
            visibleFilters: state.visibleFilters,
            openFilters: state.openFilters,
            openQuickFilters: state.openQuickFilters,
            quickFilters: state.quickFilters,
        },
    };
};

export default useForceBIFilters;
